import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CaretLeft, CaretRight, Plus, Ticket as TicketIcon } from '@phosphor-icons/react';
import { useAuth } from '../../../App';
import {
  API,
  authConfig,
  authHeaders,
  ticketAssigneeLabel,
  ticketCategoryLabel,
  ticketClientBranchLabel,
  parseTicketListResponse,
  TICKET_LIST_PAGE_SIZE,
} from '../api/ticketsApi';
import { formatUtcToIst } from '../utils/datetime';
import TicketStatusBadge from '../components/TicketStatusBadge';
import TicketDomainBadge from '../components/TicketDomainBadge';
import ListSearch from '../../../components/ListSearch';
import TicketStatusTabs, {
  statusTabToQuery,
  queryToStatusTab,
  TICKET_STATUS_TABS,
} from '../components/TicketStatusTabs';
import {
  PageHeader,
  Toolbar,
  TicketTable,
  SkeletonBlock,
  EmptyState,
  Alert,
  CountChip,
} from '../ui';
import { TicketDetailLink } from '../ticketNav';
import { useProductFilter } from '../ProductFilterContext';

function TicketListPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { productId: productFilter, selectedProduct, withProductParam } = useProductFilter();
  const canBulk = ['super_admin', 'admin', 'manager'].includes(user?.role);
  const canBulkDelete = ['super_admin', 'admin'].includes(user?.role);
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [statusTab, setStatusTab] = useState(() => queryToStatusTab(searchParams));
  const [selected, setSelected] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [bulkAssignee, setBulkAssignee] = useState('');
  const [bulkReason, setBulkReason] = useState('');
  const [bulkMsg, setBulkMsg] = useState(null);
  const [bulkError, setBulkError] = useState(null);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  useEffect(() => {
    setStatusTab(queryToStatusTab(searchParams));
  }, [searchParams]);

  const domainFilter = searchParams.get('domain') || '';
  const clientFilter = searchParams.get('client') || '';
  const priorityFilter = searchParams.get('priority') || '';
  const assignedToFilter = searchParams.get('assigned_to') || '';

  const fetchTickets = useCallback(async (q, tab, pageNum) => {
    setLoading(true);
    try {
      const hasSearch = Boolean(q && q.trim());
      const params = {
        // While searching, ignore status tab so matches across all statuses show up.
        ...(hasSearch ? {} : statusTabToQuery(tab)),
        limit: TICKET_LIST_PAGE_SIZE,
        skip: (pageNum - 1) * TICKET_LIST_PAGE_SIZE,
      };
      if (hasSearch) params.search = q.trim();
      if (domainFilter) params.domain = domainFilter;
      if (productFilter && productFilter !== '__none__') params.product_id = productFilter;
      if (clientFilter) params.client = clientFilter;
      if (priorityFilter && priorityFilter !== 'all') params.priority = priorityFilter;
      if (assignedToFilter) params.assigned_to = assignedToFilter;
      const res = await axios.get(`${API}/`, authConfig(params));
      const { items, total: count } = parseTicketListResponse(res);
      setTickets(items);
      setTotal(count);
      setSelected([]);
    } catch (err) {
      console.error(err);
      setTickets([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [domainFilter, productFilter, clientFilter, priorityFilter, assignedToFilter]);

  // Debounced API search + pagination
  useEffect(() => {
    const t = setTimeout(() => fetchTickets(search, statusTab, page), 300);
    return () => clearTimeout(t);
  }, [search, statusTab, page, fetchTickets]);

  useEffect(() => {
    setPage(1);
  }, [domainFilter, productFilter, clientFilter, priorityFilter, assignedToFilter]);

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handleStatusTabChange = (tab) => {
    setStatusTab(tab);
    setPage(1);
    const next = new URLSearchParams(searchParams);
    next.delete('status_group');
    if (tab === 'all') next.delete('status');
    else next.set('status', tab);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (!canBulk) return;
    axios.get(`${API}/assignments/engineers`, authHeaders())
      .then((res) => setEngineers(res.data || []))
      .catch(() => setEngineers([]));
  }, [canBulk]);

  const toggleSelect = (ticketNo) => {
    setSelected((prev) => (prev.includes(ticketNo) ? prev.filter((n) => n !== ticketNo) : [...prev, ticketNo]));
  };

  const toggleSelectAll = () => {
    if (selected.length === tickets.length) {
      setSelected([]);
    } else {
      setSelected(tickets.map((t) => t.ticket_no));
    }
  };

  const handleBulkAssign = async () => {
    if (!selected.length || !bulkAssignee) {
      setBulkError('Select tickets and an assignee');
      return;
    }
    setBulkSubmitting(true);
    setBulkError(null);
    setBulkMsg(null);
    try {
      const res = await axios.post(`${API}/bulk/assign`, {
        ticket_nos: selected,
        assignee_ids: [bulkAssignee],
        reason: bulkReason || null,
      }, authHeaders());
      setBulkMsg(`Assigned ${res.data.success_count} ticket(s)`);
      setSelected([]);
      await fetchTickets(search, statusTab, page);
    } catch (err) {
      setBulkError(err.response?.data?.detail || 'Bulk assign failed');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleBulkClose = async () => {
    if (!selected.length || !bulkReason.trim()) {
      setBulkError('Select tickets and enter a close reason');
      return;
    }
    setBulkSubmitting(true);
    setBulkError(null);
    setBulkMsg(null);
    try {
      const res = await axios.post(`${API}/bulk/close`, {
        ticket_nos: selected,
        reason: bulkReason,
      }, authHeaders());
      setBulkMsg(`Closed ${res.data.success_count} ticket(s)`);
      setSelected([]);
      await fetchTickets(search, statusTab, page);
    } catch (err) {
      setBulkError(err.response?.data?.detail || 'Bulk close failed');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!canBulkDelete || !selected.length) return;
    const ok = window.confirm(
      `Permanently delete ${selected.length} selected ticket(s)?\n\n`
      + 'Only cancelled tickets, or open tickets with no assignee, will be deleted. '
      + 'This cannot be undone.',
    );
    if (!ok) return;
    setBulkSubmitting(true);
    setBulkError(null);
    setBulkMsg(null);
    try {
      const res = await axios.post(`${API}/bulk/delete`, {
        ticket_nos: selected,
      }, authHeaders());
      const failed = res.data.failed || [];
      const parts = [`Deleted ${res.data.success_count} ticket(s)`];
      if (failed.length) {
        const sample = failed.slice(0, 3).map((f) => `${f.ticket_no}: ${f.error}`).join('; ');
        parts.push(`${failed.length} skipped (${sample}${failed.length > 3 ? '…' : ''})`);
      }
      setBulkMsg(parts.join('. '));
      setSelected([]);
      await fetchTickets(search, statusTab, page);
    } catch (err) {
      setBulkError(err.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const tabLabel = TICKET_STATUS_TABS.find((t) => t.id === statusTab)?.label || 'All Tickets';
  const headingTitle = selectedProduct
    ? `${tabLabel} · ${selectedProduct.name}`
    : tabLabel;
  const managerName = selectedProduct?.manager?.name;
  const headingSubtitle = managerName
    ? `Manager: ${managerName}${selectedProduct.manager?.email ? ` · ${selectedProduct.manager.email}` : ''}`
    : (selectedProduct
      ? 'No manager assigned for this product'
      : 'Filter and manage service requests');

  const totalPages = Math.max(1, Math.ceil(total / TICKET_LIST_PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * TICKET_LIST_PAGE_SIZE + 1;
  const to = Math.min(page * TICKET_LIST_PAGE_SIZE, total);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const pipelineQs = new URLSearchParams({
    ...(domainFilter ? { domain: domainFilter } : {}),
    ...(productFilter ? { product_id: productFilter } : {}),
    ...(clientFilter ? { client: clientFilter } : {}),
    ...(assignedToFilter ? { assigned_to: assignedToFilter } : {}),
  }).toString();

  return (
    <div className="tk-list-page">
      <PageHeader
        title={headingTitle}
        subtitle={headingSubtitle}
        meta={!loading ? (
          <CountChip>
            {total === 0 ? '0 results' : `${from}–${to} of ${total}`}
          </CountChip>
        ) : null}
        actions={(
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Link
              to={pipelineQs ? `/dashboard/tickets/pipeline?${pipelineQs}` : withProductParam('/dashboard/tickets/pipeline')}
              className="tk-btn tk-btn-secondary tk-btn-sm"
            >
              Pipeline
            </Link>
            <Link to={withProductParam('/dashboard/tickets/new')} className="tk-btn tk-btn-primary tk-btn-sm">
              <Plus size={14} weight="bold" />
              New Ticket
            </Link>
          </div>
        )}
      />

      <Toolbar>
        <TicketStatusTabs value={statusTab} onChange={handleStatusTabChange} />
        <div style={{ flex: 1, minWidth: '200px', maxWidth: '520px' }}>
          <ListSearch
            value={search}
            onChange={handleSearchChange}
            placeholder="Search by ticket no, assignee, category, subject…"
            resultCount={tickets.length}
            totalCount={total}
            maxWidth="520px"
            style={{ marginBottom: 0 }}
            inputStyle={{
              padding: '7px 24px 7px 32px',
              fontSize: '13px',
              borderRadius: '999px',
              border: '1px solid #C9CED8',
              background: '#fff',
              boxShadow: '0 1px 2px rgba(26,28,30,0.04)',
            }}
          />
        </div>
      </Toolbar>

      {(domainFilter || clientFilter || assignedToFilter || (priorityFilter && priorityFilter !== 'all')) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#6B7280' }}>Filters:</span>
          {domainFilter && (
            <span className="badge" style={{ background: '#EFF6FF', color: '#1D4ED8' }}>Domain: {domainFilter}</span>
          )}
          {clientFilter && (
            <span className="badge" style={{ background: '#FEF3C7', color: '#B45309' }}>Client: {clientFilter}</span>
          )}
          {assignedToFilter && (
            <span className="badge" style={{ background: '#F3E8FF', color: '#6B21A8' }}>Assignee: {assignedToFilter}</span>
          )}
          {priorityFilter && priorityFilter !== 'all' && (
            <span className="badge" style={{ background: '#F3F4F6', color: '#374151' }}>Priority: {priorityFilter}</span>
          )}
          <button
            type="button"
            className="tk-btn tk-btn-ghost"
            style={{ fontSize: 12 }}
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              ['domain', 'client', 'priority', 'assigned_to'].forEach((k) => next.delete(k));
              setSearchParams(next, { replace: true });
            }}
          >
            Clear filters
          </button>
          <Link
            to={pipelineQs ? `/dashboard/tickets/pipeline?${pipelineQs}` : withProductParam('/dashboard/tickets/pipeline')}
            className="tk-btn tk-btn-secondary"
            style={{ fontSize: 12 }}
          >
            Open pipeline
          </Link>
        </div>
      )}

      {canBulk && selected.length > 0 && (
        <div className="tk-bulk-bar">
          <div className="tk-bulk-bar-title">{selected.length} ticket{selected.length === 1 ? '' : 's'} selected</div>
          {bulkError && <Alert variant="error">{bulkError}</Alert>}
          {bulkMsg && <Alert variant="success">{bulkMsg}</Alert>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' }}>
            <div style={{ minWidth: 200 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Assign to</label>
              <select
                className="input-field"
                value={bulkAssignee}
                onChange={(e) => setBulkAssignee(e.target.value)}
                style={{ width: '100%', padding: '6px 8px' }}
              >
                <option value="">Select engineer</option>
                {engineers.map((e) => (
                  <option key={e.user_id} value={e.user_id}>{e.name || e.email || e.user_id}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 180 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Reason</label>
              <input
                className="input-field"
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)}
                placeholder="Optional for assign · required for close"
                style={{ width: '100%', padding: '6px 8px' }}
              />
            </div>
            <button type="button" className="tk-btn tk-btn-primary" disabled={bulkSubmitting} onClick={handleBulkAssign}>
              Bulk assign
            </button>
            <button type="button" className="tk-btn tk-btn-secondary" disabled={bulkSubmitting} onClick={handleBulkClose}>
              Bulk close
            </button>
            {canBulkDelete && (
              <button
                type="button"
                className="tk-btn tk-btn-secondary"
                style={{ color: '#B91C1C', borderColor: '#FECACA' }}
                disabled={bulkSubmitting}
                onClick={handleBulkDelete}
                title="Delete cancelled or open-unassigned tickets (admin only)"
              >
                Delete
              </button>
            )}
            <button type="button" className="tk-btn tk-btn-ghost" onClick={() => setSelected([])}>
              Clear
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <SkeletonBlock rows={8} />
      ) : tickets.length === 0 ? (
        <EmptyState
          icon={TicketIcon}
          title="No tickets found"
          description="Try another status, product, or search term."
          action={(
            <Link to="/dashboard/tickets/new" className="tk-btn tk-btn-primary tk-btn-sm">
              Create one
            </Link>
          )}
        />
      ) : (
        <>
          <TicketTable>
            <thead>
              <tr>
                {canBulk && (
                  <th style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      checked={selected.length === tickets.length && tickets.length > 0}
                      onChange={toggleSelectAll}
                      aria-label="Select all"
                    />
                  </th>
                )}
                <th>Ticket No</th>
                <th>Domain</th>
                <th>Category</th>
                <th>Client / Branch</th>
                <th>Assignee</th>
                <th>Subject</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Created (IST)</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => (
                <tr key={t.ticket_no}>
                  {canBulk && (
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.includes(t.ticket_no)}
                        onChange={() => toggleSelect(t.ticket_no)}
                        aria-label={`Select ${t.ticket_no}`}
                      />
                    </td>
                  )}
                  <td>
                    <TicketDetailLink ticketNo={t.ticket_no} style={{ color: '#002FA7', fontWeight: 600 }}>
                      {t.ticket_no}
                    </TicketDetailLink>
                  </td>
                  <td><TicketDomainBadge domain={t.domain} /></td>
                  <td>{ticketCategoryLabel(t)}</td>
                  <td title={ticketClientBranchLabel(t)} style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ticketClientBranchLabel(t)}
                  </td>
                  <td>{ticketAssigneeLabel(t)}</td>
                  <td title={t.subject} style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.subject}
                  </td>
                  <td><TicketStatusBadge status={t.status} /></td>
                  <td style={{ textTransform: 'capitalize' }}>{t.priority}</td>
                  <td style={{ whiteSpace: 'nowrap', color: '#4B5563' }}>{formatUtcToIst(t.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </TicketTable>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <span style={{ fontSize: 12, color: '#6B7280' }}>
              Page {page} of {totalPages}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="tk-btn tk-btn-secondary tk-btn-sm" disabled={!canPrev} onClick={() => setPage((p) => p - 1)}>
                <CaretLeft size={14} /> Prev
              </button>
              <button type="button" className="tk-btn tk-btn-secondary tk-btn-sm" disabled={!canNext} onClick={() => setPage((p) => p + 1)}>
                Next <CaretRight size={14} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default TicketListPage;
